import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  getFilteredDatasetLength,
  getFrequencyDataSetLength,
  getFrequencyCategoricalParametersName,
  getVisibleNumericalSelectedParametersNames,
  getVisibleCategoricalSelectedParametersNames
} from 'store/dataset';

import * as strings from './Strings';

import AlertPanel from './Component';

class AlertPanelContainer extends React.PureComponent {

  static propTypes = {
    graphType: PropTypes.string.isRequired,
    isFirstParamNumerical: PropTypes.bool,
    isFirstParamCategorical: PropTypes.bool,
    isSecondParamNumerical: PropTypes.bool,
    isSecondParamCategorical: PropTypes.bool,
    datasetLength: PropTypes.number.isRequired,
    categoricalArray: PropTypes.arrayOf(PropTypes.string),
    numericalArray: PropTypes.arrayOf(PropTypes.string),
    isFrequencyTable: PropTypes.bool.isRequired
  }

  buildHistogramAndBoxPlotAlerts = graphType => {
    const { numericalArray, isFirstParamNumerical, categoricalArray, isSecondParamNumerical, isFrequencyTable } = this.props;

    let alertToRender = null;

    const mixedVarGraphs = graphType === strings.graphTypes.histogram ? 'Grouped Histogram' : 'Side-by-side Box Plot';

    if (isFrequencyTable) {
      return <AlertPanel errMsg={strings.errMsgFrequencyTable(graphType)} variant="warning" />;
    }

    if (!numericalArray.length > 0) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNoVarInDataset(graphType, 'numerical', 'Numerical')} variant="danger" />
      );
    }
    if (!isFirstParamNumerical) {
      if (categoricalArray.length > 0 && numericalArray.length > 0) {
        alertToRender = (
          <>
            <AlertPanel errMsg={strings.defaultExplanatoryMsg(graphType, 'numerical')} />
            <AlertPanel errMsg={strings.errMsgSecondTypeOfGraph(mixedVarGraphs, 'categorical')} />
          </>
        );
      } else if (!categoricalArray.length > 0 && numericalArray.length > 0) {
        alertToRender = (
          <>
            <AlertPanel errMsg={strings.defaultExplanatoryMsg(graphType, 'numerical')} />
            <AlertPanel errMsg={strings.errMsgNoVarInDataset(mixedVarGraphs, 'categorical', 'Categorical')} variant="danger" />
          </>
        );
      } else {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgNoVarInDataset(graphType, 'numerical', 'Numerical')} variant="danger" />
        );
      }
    } else if (isFirstParamNumerical && isSecondParamNumerical) {
      if (categoricalArray.length > 0) {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgSecondTypeOfGraph(mixedVarGraphs, 'categorical')} />
        );
      } else {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgNoVarInDataset(mixedVarGraphs, 'categorical', 'Categorical')} variant="danger" />
        );
      }
    }

    return alertToRender;
  }

  buildPieChartAlerts = () => {
    const { categoricalArray, isFirstParamCategorical } = this.props;

    let alertToRender = null;

    if (!categoricalArray.length > 0) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNoVarInDataset('Pie Chart', 'categorical', 'Categorical')} variant="danger" />
      );
    } else if (!isFirstParamCategorical) {

      alertToRender = (
        <AlertPanel errMsg={strings.defaultExplanatoryMsg('Pie Chart', 'categorical')} />
      );
    }

    return alertToRender;
  }

  buildDotPlotAlerts = () => {
    const { numericalArray, isFirstParamNumerical, isFrequencyTable } = this.props;

    let alertToRender = null;

    if (isFrequencyTable) {
      return <AlertPanel errMsg={strings.errMsgFrequencyTable('Dot Plot')} variant="warning" />;
    }

    if (!numericalArray.length > 0) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNoVarInDataset('Dot Plot', 'numerical', 'Numerical')} variant="danger" />
      );
    } else if (!isFirstParamNumerical) {

      alertToRender = (
        <AlertPanel errMsg={strings.defaultExplanatoryMsg('Dot Plot', 'numerical')} />
      );
    }

    return alertToRender;
  }

  buildScatterPlotAlerts = () => {
    const { numericalArray, isFirstParamNumerical, isSecondParamNumerical, isFrequencyTable } = this.props;

    let alertToRender = null;

    if (isFrequencyTable) {
      return <AlertPanel errMsg={strings.errMsgFrequencyTable('Scatter Plot')} variant="warning" />;
    }

    if (!numericalArray.length > 0) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNoVarInDataset('Scatter Plot', 'numerical', 'Numerical')} variant="danger" />
      );
    } else if (numericalArray.length === 1) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNeedAtLeastTwoVarsInDataset('Scatter Plot', 'numerical', 'Numerical')} variant="danger" />
      );
    } else if (numericalArray.length < 1) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgScatterPlotOneValue} variant="danger" />
      );
    } else if (!(isFirstParamNumerical && isSecondParamNumerical)) {

      alertToRender = (
        <AlertPanel errMsg={strings.errMsgScatterPlot} />
      );
    }

    return alertToRender;
  }

  buildBarChartAlerts = () => {
    const { categoricalArray, isFirstParamCategorical, isSecondParamCategorical, isFrequencyTable } = this.props;
    let alertToRender = null;

    if (!isFirstParamCategorical && !isSecondParamCategorical) {
      if (categoricalArray.length > 1) {
        alertToRender = (
          <>
            <AlertPanel errMsg={strings.defaultExplanatoryMsg('Bar Chart', 'categorical')} />
            <AlertPanel errMsg={strings.errMsgSecondTypeOfGraph('Stacked Bar Chart', 'categorical')} />
          </>
        );
      } else if (categoricalArray.length === 1) {
        alertToRender = (
          <>
            <AlertPanel errMsg={strings.defaultExplanatoryMsg('Bar Chart', 'categorical')} />
            {!isFrequencyTable && <AlertPanel errMsg={strings.errMsgNeedAtLeastTwoVarsInDataset('Stacked Bar Chart', 'categorical', 'Categorical')} variant="danger" />}
          </>
        );
      } else {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgNoVarInDataset('Bar Chart', 'categorical', 'Categorical')} variant="danger" />
        );
      }
    } else if (isFirstParamCategorical && !isSecondParamCategorical) {
      if (categoricalArray.length > 1) {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgSecondTypeOfGraph('Stacked Bar Chart', 'categorical')} />
        );
      } else {
        alertToRender = (
          <AlertPanel errMsg={strings.errMsgNeedAtLeastTwoVarsInDataset('Stacked Bar Chart', 'categorical', 'Categorical')} variant="danger" />
        );
      }
    } else if (!isFirstParamCategorical && isSecondParamCategorical) {
      alertToRender = (
        <AlertPanel errMsg={strings.defaultExplanatoryMsg('Bar Chart', 'categorical')} />
      );
    }

    return alertToRender;
  }

  buildProbabilityPlot = () => {
    const { numericalArray, isFirstParamNumerical, isFrequencyTable } = this.props;

    let alertToRender = null;
    const { probabilityPlot } = strings.graphTypes;
    if (isFrequencyTable) {
      alertToRender = <AlertPanel errMsg={strings.errMsgFrequencyTable(probabilityPlot)} variant="warning" />;
    } else if (!numericalArray.length > 0) {
      alertToRender = (
        <AlertPanel errMsg={strings.errMsgNoVarInDataset(probabilityPlot, 'numerical', 'Numerical')} variant="danger" />
      );
    } else if (!isFirstParamNumerical) {
      alertToRender = (
        <AlertPanel errMsg={strings.defaultExplanatoryMsg(probabilityPlot, 'numerical')} />
      );
    }
    return alertToRender;
  }

  render () {
    const { datasetLength, categoricalArray, numericalArray, graphType } = this.props;
    const { graphTypes } = strings;

    if (datasetLength === 0 || (!categoricalArray.length > 0 && !numericalArray.length > 0)) {
      // dataset is empty, hence return the same generic error that does not depend on graph type
      return (
        <AlertPanel errMsg={strings.errMsgEmptyDataset} variant="danger" />
      );
    }

    switch (graphType) {
      case graphTypes.histogram: {
        return this.buildHistogramAndBoxPlotAlerts(graphTypes.histogram);
      }
      case graphTypes.dotPlot: {
        return this.buildDotPlotAlerts();
      }
      case graphTypes.scatterPlot: {
        return this.buildScatterPlotAlerts();
      }
      case graphTypes.barChart: {
        return this.buildBarChartAlerts();
      }
      case graphTypes.pieChart: {
        return this.buildPieChartAlerts();
      }
      case graphTypes.boxPlot: {
        return this.buildHistogramAndBoxPlotAlerts(graphTypes.boxPlot);
      }
      case graphTypes.probabilityPlot: {
        return this.buildProbabilityPlot(graphTypes.probabilityPlot);
      }
      default: {
        // figure out a way to deal with unexpected values
        return null;
      }
    }
  }

}

const mapStateToProps = (state, ownProps) => (ownProps.isFrequencyTable ? {
  datasetLength: getFrequencyDataSetLength(state),
  numericalArray: null,
  categoricalArray: getFrequencyCategoricalParametersName(state)
} : {
  datasetLength: getFilteredDatasetLength(state),
  numericalArray: getVisibleNumericalSelectedParametersNames(state),
  categoricalArray: getVisibleCategoricalSelectedParametersNames(state)
});

export default connect(mapStateToProps)(AlertPanelContainer);
